import { productDecode } from '@/lib/shopify/gid';
import { getCustomProductRecommendations } from '@/lib/shopify';

// Constants for product IDs
const ULTRA_COOL_PROTECTOR_ID = '6881024770146';
const BED_FRAME_ID = '1673214656610';
const PLATFORM_BED_ID = '1673217343586';
const FOUNDATION_ID = '1673215574114';
const ADJUSTABLE_BASE_ID = '14622108451186';
const ADJUSTABLE_BASE_PRO_ID = '6601612165218';
const COTTON_BLEND_SHEET_SET_ID = '4168235122786';

function getRecsForScenario(scenario) {
  const onlyBasesOrMattressesInCartUpsell = [
    ULTRA_COOL_PROTECTOR_ID,
    PLATFORM_BED_ID,
    FOUNDATION_ID
  ];
  const platformBedAndAdjustableBaseWithProtectorCartUpsell = [
    COTTON_BLEND_SHEET_SET_ID
  ];
  const platformBedAndAdjustableBaseNoProtectorCartUpsell = [
    ULTRA_COOL_PROTECTOR_ID,
    COTTON_BLEND_SHEET_SET_ID
  ];

  const mattressAndFoundationNoProtector = [
    ULTRA_COOL_PROTECTOR_ID,
    COTTON_BLEND_SHEET_SET_ID,
    BED_FRAME_ID
  ];

  const mattressAndFoundationWithProtector = [
    BED_FRAME_ID,
    COTTON_BLEND_SHEET_SET_ID
  ];

  const mattressAndFrame = [
    ULTRA_COOL_PROTECTOR_ID,
    FOUNDATION_ID,
    COTTON_BLEND_SHEET_SET_ID
  ];

  const onlyBeddingInCartUpsell = [
    ULTRA_COOL_PROTECTOR_ID,
    COTTON_BLEND_SHEET_SET_ID
  ];

  const defaultUpSellItems = [
    PLATFORM_BED_ID,
    FOUNDATION_ID,
    COTTON_BLEND_SHEET_SET_ID
  ];

  switch (scenario) {
    case 'ONLY_MATTRESS':
    case 'ONLY_BASE':
      return onlyBasesOrMattressesInCartUpsell;

    case 'ONLY_BEDDING':
      return onlyBeddingInCartUpsell;

    case 'MATTRESS_WITH_BASE_PLATFORM_BED_INCLUDES_PROTECTOR':
      return platformBedAndAdjustableBaseWithProtectorCartUpsell;

    case 'MATTRESS_WITH_BASE_PLATFORM_BED_INCLUDES_NO_PROTECTOR':
      return platformBedAndAdjustableBaseNoProtectorCartUpsell;

    case 'MATTRESS_WITH_BASE_FOUNDATION_INCLUDES_PROTECTOR':
      return mattressAndFoundationWithProtector;

    case 'MATTRESS_WITH_BASE_FOUNDATION_INCLUDES_NO_PROTECTOR':
      return mattressAndFoundationNoProtector;

    case 'MATTRESS_WITH_ADJUSTABLE_BASE_INCLUDES_PROTECTOR':
      return platformBedAndAdjustableBaseWithProtectorCartUpsell;

    case 'MATTRESS_WITH_ADJUSTABLE_BASE_INCLUDES_NO_PROTECTOR':
      return platformBedAndAdjustableBaseNoProtectorCartUpsell;

    case 'MATTRESS_WITH_BASE_FRAME_INCLUDES_NO_PROTECTOR':
    case 'MATTRESS_WITH_BASE_FRAME_INCLUDES_PROTECTOR':
      return mattressAndFrame;

    case 'DEFAULT':
    default:
      return defaultUpSellItems;
  }
}

function getCartScenario(itemsInCart) {
  const productTypes = itemsInCart.map((item) =>
    item.productType.toLowerCase()
  );
  const productIds = itemsInCart.map((item) => item.productId);

  // only mattress (note: ignoring sheets/pillows when checking for this)
  if (
    productTypes.includes('mattress') &&
    !productTypes.includes('base') &&
    !productTypes.includes('bedding')
  ) {
    return 'ONLY_MATTRESS';
  }

  // only base in cart (note: ignoring sheets/pillows here too)
  if (
    productTypes.includes('base') &&
    !productTypes.includes('mattress') &&
    !productTypes.includes('bedding')
  ) {
    return 'ONLY_BASE';
  }

  // only bedding in cart (consider sheets and pillows as bedding)
  const hasBedding = productTypes.some(
    (type) => type === 'bedding' || type === 'sheet' || type === 'pillow'
  );
  if (
    hasBedding &&
    !productTypes.includes('mattress') &&
    !productTypes.includes('base')
  ) {
    return 'ONLY_BEDDING';
  }

  if (productTypes.includes('mattress') && productTypes.includes('base')) {
    // Protector is present
    if (productIds.includes(ULTRA_COOL_PROTECTOR_ID)) {
      // with base foundation
      if (productIds.includes(FOUNDATION_ID)) {
        return 'MATTRESS_WITH_BASE_FOUNDATION_INCLUDES_PROTECTOR';
      }
      // with bed frame
      if (productIds.includes(BED_FRAME_ID)) {
        return 'MATTRESS_WITH_BASE_FRAME_INCLUDES_PROTECTOR';
      }
      // with base platform bed
      if (productIds.includes(PLATFORM_BED_ID)) {
        return 'MATTRESS_WITH_BASE_PLATFORM_BED_INCLUDES_PROTECTOR';
      }
      // with adjustable/adjustable pro base
      if (
        productIds.includes(ADJUSTABLE_BASE_ID) ||
        productIds.includes(ADJUSTABLE_BASE_PRO_ID)
      ) {
        return 'MATTRESS_WITH_ADJUSTABLE_BASE_INCLUDES_PROTECTOR';
      }
    }

    // no protector with bed frame
    if (productIds.includes(BED_FRAME_ID)) {
      return 'MATTRESS_WITH_BASE_FRAME_INCLUDES_NO_PROTECTOR';
    }
    // no protector with base foundation
    if (productIds.includes(FOUNDATION_ID)) {
      return 'MATTRESS_WITH_BASE_FOUNDATION_INCLUDES_NO_PROTECTOR';
    }
    // no protector with base platform bed
    if (productIds.includes(PLATFORM_BED_ID)) {
      return 'MATTRESS_WITH_BASE_PLATFORM_BED_INCLUDES_NO_PROTECTOR';
    }
    // no protector with adjustable/adjustable pro base
    if (
      productIds.includes(ADJUSTABLE_BASE_ID) ||
      productIds.includes(ADJUSTABLE_BASE_PRO_ID)
    ) {
      return 'MATTRESS_WITH_ADJUSTABLE_BASE_INCLUDES_NO_PROTECTOR';
    }
  }

  return 'DEFAULT';
}

/**
 * Returns product recommendations based on the current cart scenario.
 *
 * [ROY] I tried to avoid updating the existing logic too much because I
 * understand the flow will be updated soon. While it contains a lot of hardcoded
 * situations it makes sense to avoid over-engineering the logic for now.
 *
 * @param productRecommendations - Array of product objects from Shopify from
 *   containing the products that we want to recommend.
 * @param itemsInCart - Object that looks like [{ productType, productId }, ...]
 */
export function filterRecommendationProducts(
  productRecommendations,
  itemsInCart
) {
  const safeRecommendations = (productRecommendations || []).filter((product) =>
    Boolean(product?.id)
  );

  const cartScenario = getCartScenario(itemsInCart);
  const recsForScenario = getRecsForScenario(cartScenario);

  // get the full product objects for the recommendations
  const filteredProducts = recsForScenario
    .map((productId) => {
      return safeRecommendations.find((product) => {
        return productDecode(product.id)?.id === productId;
      });
    })
    .filter((product) => {
      if (!product?.id) return false;

      // filter out products already in the cart
      return !itemsInCart.some(
        (item) => productDecode(product.id)?.id === item.productId
      );
    });

  return filteredProducts;
}

export async function getRecommendationsFromCart(cart) {
  const recommendations = { products: [], cartProductSize: '' };

  // find the product variant size for first item in cart
  const firstVariant = cart.lines[0];
  const sizeOption = firstVariant.merchandise.selectedOptions.find(
    (option) => option.name.toLowerCase() === 'size'
  );

  let firstVariantSize = sizeOption ? sizeOption.value.toLowerCase() : null;
  if (firstVariantSize && firstVariantSize.includes('/')) {
    // handle cases like 'standard/queen' for pillows
    if (firstVariantSize.includes('standard/')) {
      firstVariantSize = firstVariantSize.replace('standard/', '').trim();
    } else {
      // otherwise take the first part of the size
      firstVariantSize = firstVariantSize.split('/')[0].trim();
    }
  }

  recommendations.cartProductSize = firstVariantSize;

  const findProductTypes = cart.lines.map((item) => ({
    productType: item.merchandise.product.productType.toLowerCase(),
    productId: item.merchandise.product.id.split('/').pop()
  }));

  const uniqueProductTypes = [...new Set(findProductTypes)];
  const response = await getCustomProductRecommendations(
    cart.lines[0].merchandise.product.id
  );
  if (response.products) {
    // [ROY] Note this matches using hardcoded product IDs and uses the above
    // response to get the full product objects which are a bit large. Plus, the
    // cart cross sell section itself comes with product data (for tabs and such).
    // Maybe we should use just one slim source of data for all of this logic.
    const products = filterRecommendationProducts(
      response.products,
      uniqueProductTypes
    );
    recommendations.products = products;
  }

  return recommendations;
}
